/**
 * V2InputBar — InputBar and sub-components
 * Phase 5 (PRI-291): extracted from PrimateVisionV2.tsx
 *
 * Contains: StreamChipPreview, CameraSwitcher, InputBar,
 *           SubtleToggle, ToolSep, ModelToggle, VideoSourcePopover, SrcOption
 */

import { useState, useRef, useEffect, memo } from 'react';
import { L, MODEL_DISPLAY_NAME, modelStatusLabel, type VideoSource, type QuotaBarData, type CostPreviewData } from './pv2-shared';
import type { CompiledQuery, ParseState } from './compiled-query-types';
import { generatePreviewText } from './compiled-query-types';
import { CompiledQueryCard } from './CompiledQueryCard';
import { BatchComposer } from './BatchComposer';
import { trackBatchModeToggled, trackUpgradeFromQuotaWall } from '../../telemetry/events';

/**
 * SessionCountdown (PRI-488 F4) — live "m:ss session time left" from the
 * metering tick's canonical session_remaining_s field. Server ticks arrive
 * every 5s; a 1s local timer interpolates between ticks so the display
 * counts down smoothly. Re-syncs on every server tick (server is truth).
 */
export function SessionCountdown({ sessionRemainingS }: { sessionRemainingS: number }) {
  const [display, setDisplay] = useState(sessionRemainingS);
  const anchorRef = useRef<{ value: number; at: number }>({ value: sessionRemainingS, at: Date.now() });

  useEffect(() => {
    anchorRef.current = { value: sessionRemainingS, at: Date.now() };
    setDisplay(sessionRemainingS);
  }, [sessionRemainingS]);

  useEffect(() => {
    const iv = setInterval(() => {
      const { value, at } = anchorRef.current;
      const elapsed = Math.floor((Date.now() - at) / 1000);
      setDisplay(Math.max(0, value - elapsed));
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const m = Math.floor(display / 60);
  const s = Math.floor(display % 60);
  const low = display <= 60;
  return (
    <span
      data-testid="session-countdown"
      title="Time left in this session's credit reservation — not your account balance"
      style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: low ? '#DC2626' : L.textMuted }}
    >
      ⏱ {m}:{s.toString().padStart(2, '0')} session time left
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// MobilePromptPreview — PRI-494 fix (2026-07-29, Matt: "Input shows json and
// resizes"). On mobile, the compiled-query area is a SINGLE fixed-height,
// human-readable line for EVERY parse state so the composer never jumps:
//   idle          → "type a prompt to see how Darwin reads it"
//   parsing       → "Darwin is reading your prompt…"
//   lanes_visible → generatePreviewText() summary (tap to expand full lanes)
//   error         → the parse error message
// It never renders skeleton lanes or the monospace lane stack (which read as
// raw JSON to users). The full CompiledQueryCard is opt-in via tap.
// ───────────────────────────────────────────────────────────────────────────────

/** Fixed row height (px) — identical across all parse states (zero layout shift). */
export const MOBILE_PROMPT_PREVIEW_HEIGHT = 28;

export function MobilePromptPreview({ parseState, compiledQuery, parseError, expanded, onToggleExpanded }: {
  parseState: ParseState;
  compiledQuery: CompiledQuery | null;
  parseError?: string | null;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const interactive = (parseState === 'lanes_visible' || parseState === 'edit') && !!compiledQuery;

  let text: string;
  let color = '#7A7669';
  let italic = true;
  if (parseState === 'parsing') {
    text = 'Darwin is reading your prompt…';
  } else if (parseState === 'error') {
    text = parseError ?? "Darwin doesn't understand this prompt — try something more specific";
    color = '#DC2626';
    italic = false;
  } else if (interactive) {
    text = generatePreviewText(compiledQuery!);
  } else {
    text = 'type a prompt to see how Darwin reads it';
  }

  return (
    <div
      data-testid="mobile-prompt-preview"
      role={interactive ? 'button' : undefined}
      aria-expanded={interactive ? expanded : undefined}
      aria-label={interactive ? 'How Darwin reads your prompt — tap for details' : undefined}
      onClick={interactive ? onToggleExpanded : undefined}
      style={{
        height: MOBILE_PROMPT_PREVIEW_HEIGHT,
        margin: '0 10px 6px',
        padding: '0 10px',
        borderRadius: 6,
        border: `1px dashed ${parseState === 'error' ? '#DC2626' : '#D4D0C8'}`,
        opacity: parseState === 'idle' ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        overflow: 'hidden',
        boxSizing: 'border-box',
        cursor: interactive ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span
        data-testid="mobile-prompt-preview-text"
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          fontFamily: 'Inter, -apple-system, sans-serif',
          fontStyle: italic ? 'italic' : 'normal',
          color,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {text}
      </span>
      {interactive && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
          <path d="M2 4l3 3 3-3" stroke="#7A7669" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

// Memoised so typing in the prompt textarea does not cause flash re-renders.
// Only re-renders when localStream or frozenFrameUrl actually change.
export const StreamChipPreview = memo(function StreamChipPreview({ localStream, frozenFrameUrl, isFileStream = false, isFrontCamera = false }: { localStream: MediaStream | null; frozenFrameUrl: string | null; isFileStream?: boolean; isFrontCamera?: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // useEffect keeps srcObject in sync when localStream changes (e.g. device switch)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (localStream) {
      el.srcObject = localStream;
      el.muted = true;
      // play() may fail in Safari outside user-gesture context - suppress
      el.play().catch(() => {});
    } else {
      el.srcObject = null;
    }
  }, [localStream]);

  // Stream stopped and we have a frozen frame - show it as a static image
  if (!localStream && frozenFrameUrl) {
    return (
      <img src={frozenFrameUrl} alt="Last frame" style={{ width: 48, height: 34, borderRadius: 4, objectFit: 'cover', flexShrink: 0, opacity: 0.8 }} />
    );
  }

  if (!localStream) {
    // Awaiting camera permission - show pulsing red dot
    return (
      <div style={{ width: 48, height: 34, borderRadius: 4, background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', animation: 'pv2-blink 1.2s ease-in-out infinite' }} />
      </div>
    );
  }

  // Safari requires: muted attribute (not just prop), playsinline, webkit-playsinline,
  // and srcObject set via ref callback synchronously during mount (closest to user gesture).
  // We set srcObject in both the ref callback (synchronous, within gesture context)
  // AND in useEffect (reactive, for stream changes).
  // File streams should NOT be mirrored (only camera selfie view is mirrored).
  return (
    <video
      data-testid="stream-chip-preview"
      ref={el => {
        videoRef.current = el;
        if (el && localStream) {
          // Set srcObject synchronously in ref callback - Safari needs this
          // to be as close to the user gesture as possible
          el.srcObject = localStream;
          el.muted = true;
          el.play().catch(() => {});
        }
      }}
      style={{ width: 48, height: 34, borderRadius: 4, objectFit: 'cover', flexShrink: 0, background: '#000', transform: (!isFileStream && isFrontCamera) ? 'scaleX(-1)' : 'none' }}
      muted
      playsInline
      autoPlay
      // Safari-specific attributes (React doesn't forward unknown props to DOM, use as any)
      {...({ 'webkit-playsinline': 'true' } as React.HTMLAttributes<HTMLVideoElement>)}
    />
  );
});


// Short display form of a camera label for the chip trigger (discoverability, 2026-07-10):
// strips parentheticals ("FaceTime HD Camera (Built-in)" → "FaceTime HD Camera")
// and truncates at ~18 chars so the chip stays compact.
export function shortCameraLabel(label: string | undefined | null, fallback = 'Camera'): string {
  const raw = (label ?? '').replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  if (!raw) return fallback;
  return raw.length > 18 ? raw.slice(0, 17).trimEnd() + '…' : raw;
}

// Camera switcher - small dropdown for switching between multiple cameras on the stream source chip
export function CameraSwitcher({ devices, selectedDeviceId, onSelect, displayLabel, onOpenChange }: {
  devices: MediaDeviceInfo[];
  selectedDeviceId: string | null;
  onSelect: (deviceId: string) => void;
  /** Discoverability (2026-07-10, Matt feedback): when set, the trigger renders the
      selected camera name + a chevron-down next to the icon so users with multiple
      cameras can actually find the picker. Omit for the legacy tiny-icon-only trigger. */
  displayLabel?: string;
  /** Notified when the dropdown opens/closes (used to dismiss the first-use nudge). */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { onOpenChange?.(open); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        data-testid="camera-switcher-trigger"
        onClick={() => setOpen(o => !o)}
        title="Switch camera"
        aria-label="Switch camera"
        style={{
          background: 'none', border: 'none', cursor: 'pointer', color: L.textMuted, padding: '0 2px', lineHeight: 1,
          display: 'flex', alignItems: 'center', gap: 3,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <path d="M3.5 1.5h5l1 1.5H11a1 1 0 011 1v5.5a1 1 0 01-1 1H1a1 1 0 01-1-1V4a1 1 0 011-1h1.5l1-1.5z" stroke="currentColor" strokeWidth="1" fill="none"/>
          <circle cx="6" cy="6.5" r="2" stroke="currentColor" strokeWidth="1" fill="none"/>
        </svg>
        {displayLabel && (
          <>
            <span data-sentry-mask style={{ fontSize: 11, color: L.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
              {displayLabel}
            </span>
            <svg width="8" height="8" viewBox="0 0 9 9" fill="none" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
              <path d="M1.5 3l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </>
        )}
      </button>
      {open && (
        <div data-testid="camera-switcher-menu" style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 100,
          background: L.bgSecondary, border: `1px solid ${L.border}`, borderRadius: 8,
          boxShadow: '0 6px 24px rgba(0,0,0,0.15)', minWidth: 200, padding: 4,
        }}>
          <div style={{ padding: '4px 8px', fontSize: 9, fontFamily: '"JetBrains Mono", monospace', color: L.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>
            Select camera
          </div>
          {devices.map((d, i) => {
            const isSelected = d.deviceId === selectedDeviceId;
            const label = d.label || `Camera ${i + 1}`;
            return (
              <div
                key={d.deviceId || `cam-${i}`}
                data-testid="camera-switcher-option"
                onClick={() => { onSelect(d.deviceId); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                  background: isSelected ? `${L.signal}18` : 'transparent',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${L.signal}18`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected ? `${L.signal}18` : 'transparent'; }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isSelected ? L.signal : 'transparent', border: isSelected ? 'none' : `1.5px solid ${L.textMuted}`, flexShrink: 0 }} />
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: isSelected ? L.textPrimary : L.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function InputBar({
  videoSource, onClearVideo, onSwitchToWebcam, onSwitchToFile,
  prompt, onPromptChange, onSubmit,
  onOpenVideoMenu: _onOpenVideoMenu,
  onRequestFile,
  processOffline, onProcessOfflineChange,
  disabled,
  localStream,
  frozenFrameUrl,
  quotaBar,
  costPreview,
  publicVideoId,
  streamingMetering,
  isStreaming,
  promptSent = false,
  hasResultPills = false,
  isFileStream,
  fileDuration,
  fileCurrentTime,
  isMobile = false,
  compact = false,
  cameraDevices,
  selectedCameraId,
  onSelectCamera,
  sourceModeOverride,
  onSourceModeChange,
  textareaRef: externalTextareaRef,
  fillHeight = false,
  hideVideoChip = false,
  isFrontCamera = false,
  compiledQuery,
  parseState,
  parseError,
  onCompiledQueryChange,
  onEditModeToggle,
  planTier = 'free',
  stablePromptPreview = false,
  frameless = false,
}: {
  videoSource: VideoSource | null;
  onClearVideo: () => void;
  /** Called when the user explicitly clicks the Webcam toggle. Clears file state and re-acquires camera. */
  onSwitchToWebcam?: () => void;
  prompt: string;
  onPromptChange: (v: string) => void;
  onSubmit: () => void;
  onOpenVideoMenu: () => void;
  onRequestFile?: () => void;
  /** PRI-281: called when user switches to File mode — cancels any pre-connect. */
  onSwitchToFile?: () => void;
  processOffline: boolean;
  onProcessOfflineChange: (v: boolean) => void;
  disabled: boolean;
  localStream?: MediaStream | null;
  frozenFrameUrl?: string | null;
  quotaBar?: QuotaBarData | null;
  /** PRI-488 F1: pre-Run cost preview (validate_only). Rendered above the balance bar. */
  costPreview?: CostPreviewData | null;
  /** PRI-488 F7: public video id (video_…) for the batch composer. Undefined hides the batch toggle. */
  publicVideoId?: string | null;
  streamingMetering?: { daily_used_s: number; daily_remaining_s: number; session_remaining_s?: number; elapsed_s?: number } | null;
  isStreaming?: boolean;
  promptSent?: boolean;
  hasResultPills?: boolean;
  isFileStream?: boolean;
  fileDuration?: number;
  fileCurrentTime?: number;
  isMobile?: boolean;
  compact?: boolean;
  cameraDevices?: MediaDeviceInfo[];
  selectedCameraId?: string | null;
  onSelectCamera?: (deviceId: string) => void;
  /** null = no source chosen yet (first use) — both toggles render unselected + guide tooltip */
  sourceModeOverride?: 'webcam' | 'file' | null;
  onSourceModeChange?: (mode: 'webcam' | 'file') => void;
  // Optional ref forwarded from parent so clicking a suggested prompt chip focuses the textarea
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  /** When true the outer div fills its parent's height (used in BEFORE state row) */
  fillHeight?: boolean;
  /** When true, hide the video source chip (used after fly animation - webcam is in the result pill) */
  hideVideoChip?: boolean;
  /** True when the active camera is front/selfie — used to conditionally mirror the chip preview */
  isFrontCamera?: boolean;
  // Compiled query props (all optional for backward compat)
  compiledQuery?: CompiledQuery | null;
  parseState?: ParseState;
  parseError?: string | null;
  onCompiledQueryChange?: (q: CompiledQuery) => void;
  onEditModeToggle?: (open: boolean) => void;
  /** PRI: analytics tag for batch toggle + compiled query edit events */
  planTier?: string;
  /** PRI-494 (2026-07-29): render the fixed-height human-readable prompt
   *  preview (MobilePromptPreview) instead of the auto-expanding
   *  CompiledQueryCard + skeleton lanes. Used by the mobile 3-zone shell so
   *  the composer never resizes while typing and never shows raw-JSON-looking
   *  lanes. The full card stays available via tap-to-expand. */
  stablePromptPreview?: boolean;
  /** PRI-494 (2026-07-29, Matt: "Rounded edges"): drop the floating-card
   *  chrome (rounded border + shadow). The mobile 3-zone shell renders the
   *  composer as a flat full-width strip (the Option A mockup treatment) —
   *  the zone container supplies background + top border. */
  frameless?: boolean;
}) {
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Use the parent-supplied ref if provided, otherwise fall back to internal ref
  const textareaRef = (externalTextareaRef ?? internalTextareaRef) as React.RefObject<HTMLTextAreaElement | null>;
  const [sourceModeInternal, setSourceModeInternal] = useState<'webcam' | 'file'>(videoSource?.kind === 'file' ? 'file' : 'webcam');
  // NOTE: null is a meaningful override ("nothing selected yet") — only fall back
  // to internal state when the parent passes undefined (uncontrolled usage).
  const sourceMode = sourceModeOverride !== undefined ? sourceModeOverride : sourceModeInternal;
  const setSourceMode = (mode: 'webcam' | 'file') => { setSourceModeInternal(mode); onSourceModeChange?.(mode); };
  const hasVideo = videoSource !== null || sourceMode === 'webcam'; // webcam counts even before stream starts
  const hasText = prompt.trim().length > 0;
  const [editMode, setEditMode] = useState(false);
  // PRI-494: opt-in expansion of the full CompiledQueryCard on mobile.
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const canSubmit = hasVideo && hasText && !disabled && parseState !== 'parsing';
  const [showAdvanced, setShowAdvanced] = useState(false);
  // PRI-488 F7: batch composer (multi-prompt, 50% discount) — file mode only.
  const [showBatchComposer, setShowBatchComposer] = useState(false);
  const [showCameraPicker, setShowCameraPicker] = useState(false);
  const cameraPickerRef = useRef<HTMLDivElement>(null);
  // Enumerate cameras independently of permission state so the picker icon
  // appears before the user has granted camera access on first submit.
  const [enumeratedCameras, setEnumeratedCameras] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices.enumerateDevices()
      .then(devices => setEnumeratedCameras(devices.filter(d => d.kind === 'videoinput')))
      .catch(() => {});
    // Re-enumerate when devices change (camera plugged in/out)
    navigator.mediaDevices.addEventListener?.('devicechange', () => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => setEnumeratedCameras(devices.filter(d => d.kind === 'videoinput')))
        .catch(() => {});
    });
  }, []);

  // Close camera picker when clicking outside
  useEffect(() => {
    if (!showCameraPicker) return;
    function handleClick(e: MouseEvent) {
      if (cameraPickerRef.current && !cameraPickerRef.current.contains(e.target as Node)) {
        setShowCameraPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCameraPicker]);

  // Prefer the richer post-permission list; fall back to enumerated pre-permission list.
  // If neither is available yet (async enumeration pending), show a placeholder so the
  // camera icon renders immediately in webcam mode.
  const postPermissionDevices = (cameraDevices ?? []).filter(d => d.kind === 'videoinput');
  const videoCameraDevices = postPermissionDevices.length > 0
    ? postPermissionDevices
    : enumeratedCameras.length > 0
      ? enumeratedCameras
      // Fallback: create a placeholder so the icon shows before enumeration resolves
      : sourceMode === 'webcam'
        ? [{ deviceId: '', groupId: '', kind: 'videoinput' as MediaDeviceKind, label: '', toJSON: () => ({}) }]
        : [];

  // ── Camera-picker discoverability (2026-07-10, Matt feedback) ────────────
  // Matt tested live streaming with multiple cameras (Mac + iPhone Continuity)
  // and never found the switcher. Two fixes: (a) the chip switcher now shows the
  // selected camera NAME + chevron (see CameraSwitcher displayLabel), (b) a
  // one-time nudge tooltip points at the picker the first time webcam mode
  // starts with >1 cameras available.
  const CAMERA_NUDGE_KEY = 'primate-camera-picker-seen';
  const [showCameraNudge, setShowCameraNudge] = useState(false);
  const markCameraPickerSeen = () => {
    try { localStorage.setItem(CAMERA_NUDGE_KEY, '1'); } catch { /* private mode */ }
    setShowCameraNudge(false);
  };
  // Real (non-placeholder) camera count — placeholder entries have deviceId ''.
  const realCameraCount = Math.max(postPermissionDevices.length, enumeratedCameras.length);
  useEffect(() => {
    if (sourceMode !== 'webcam' || isMobile) return;
    if (realCameraCount <= 1) return;
    let seen = false;
    try { seen = !!localStorage.getItem(CAMERA_NUDGE_KEY); } catch { seen = true; }
    if (!seen) setShowCameraNudge(true);
  }, [sourceMode, realCameraCount, isMobile]);

  // Selected camera (best-effort) for the chip label + nudge copy.
  const selectedCameraDevice =
    videoCameraDevices.find(d => d.deviceId === (selectedCameraId ?? '')) ?? videoCameraDevices[0];
  const selectedCameraShortLabel = shortCameraLabel(selectedCameraDevice?.label);
  // Chip switcher renders only for stream sources with >1 post-permission cameras
  const chipCameras = (cameraDevices ?? []).filter(d => d.kind === 'videoinput');
  // Chip switcher visibility (PRI-451): the AFTER-state chip switcher renders only for
  // stream sources with >1 post-permission cameras and an unhidden chip. When it is NOT
  // visible (the common pre-stream case), the nudge anchors to the toolbar picker instead
  // — otherwise showCameraNudge could be true with nothing rendering it.
  const chipSwitcherVisible = !!videoSource && videoSource.kind === 'stream' && !hideVideoChip && chipCameras.length > 1;

  // One-time dismissible nudge, anchored to whichever picker control is on screen.
  const cameraNudge = (
    <div
      data-testid="camera-picker-nudge"
      role="status"
      style={{
        position: 'absolute' as const,
        top: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)', zIndex: 300,
        background: L.signal,
        color: '#fff',
        padding: '7px 9px 7px 11px', borderRadius: 8,
        fontSize: 12, fontWeight: 500, lineHeight: '16px',
        fontFamily: 'Inter, -apple-system, sans-serif',
        whiteSpace: 'nowrap' as const,
        boxShadow: '0 4px 16px rgba(27,94,255,0.28)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}
    >
      <span data-sentry-mask>Using {selectedCameraShortLabel} — click to switch camera</span>
      <button
        data-testid="camera-picker-nudge-dismiss"
        aria-label="Dismiss"
        onClick={(e) => { e.stopPropagation(); markCameraPickerSeen(); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#fff',
          padding: 2, lineHeight: 1, display: 'flex', alignItems: 'center', opacity: 0.85, flexShrink: 0,
        }}
      >
        <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
          <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
      {/* arrow (points up at the picker control) */}
      <div style={{
        position: 'absolute' as const, bottom: '100%', left: '50%', marginLeft: -6,
        width: 0, height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: `6px solid ${L.signal}`,
      }} />
    </div>
  );

  // Credits-era balance bar derived state (PRI-437 P8): out of credits when the
  // balance is zero; low when at/below the auto-refill threshold.
  const creditsExhausted = !!quotaBar && !quotaBar.loading && quotaBar.balanceSeconds !== null && quotaBar.balanceSeconds <= 0;
  const creditsLow = !!quotaBar && !quotaBar.loading && !creditsExhausted &&
    quotaBar.balanceSeconds !== null && quotaBar.thresholdSeconds !== null &&
    quotaBar.balanceSeconds <= quotaBar.thresholdSeconds;

  // Keep textarea at minimum lines; grow beyond that
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const minH = compact ? 36 : isMobile ? 24 : 3 * 24; // compact: single line
    const maxH = compact ? 36 : isMobile ? 3 * 24 : 240;
    el.style.height = Math.max(minH, Math.min(el.scrollHeight, maxH)) + 'px';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, isMobile, compact]);

  return (
    <>
      <style>{`
        @keyframes pv2-pulse {
          0%, 100% { box-shadow: 0 0 0 0 ${L.signal}55; }
          60% { box-shadow: 0 0 0 7px ${L.signal}00; }
        }
        @keyframes pv2-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
      <div data-testid="input-bar" style={{
        ...(frameless
          ? { background: 'transparent' }
          : {
              background: L.bgSurface,
              border: `1.5px solid ${L.border}`,
              borderRadius: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }),
        // border-box so the 1.5px border can't push width past a width:100% parent
        // (latent horizontal-overflow contributor on narrow mobile viewports)
        boxSizing: 'border-box' as const,
        maxWidth: '100%',
        ...(fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' as const } : {}),
      }}>
        {/* Video chip */}
        {hasVideo && videoSource && !hideVideoChip && (
          <div style={{ padding: '10px 14px 0', display: 'flex', gap: 8 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: L.bgSecondary, border: `1px solid ${L.border}`,
              borderRadius: 8, padding: '4px 8px', maxWidth: 280,
            }}>
              {videoSource.thumbUrl ? (
                <img src={videoSource.thumbUrl} alt="" style={{ width: 28, height: 20, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
              ) : videoSource.kind === 'stream' ? (
                <StreamChipPreview localStream={localStream ?? null} frozenFrameUrl={frozenFrameUrl ?? null} isFileStream={isFileStream} isFrontCamera={isFrontCamera} />
              ) : (
                <div style={{ width: 28, height: 20, borderRadius: 4, background: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', animation: 'pv2-blink 1.2s ease-in-out infinite' }} />
                </div>
              )}
              <span data-sentry-mask style={{ fontSize: 12, color: L.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                {videoSource.name}
              </span>
              {/* Camera switcher - selected camera name + chevron when multiple cameras and
                  this is a stream source (2026-07-10: was an unlabelled 12px icon nobody found) */}
              {chipSwitcherVisible && (
                <div style={{ position: 'relative' as const, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <CameraSwitcher
                    devices={chipCameras}
                    selectedDeviceId={selectedCameraId ?? null}
                    onSelect={onSelectCamera!}
                    displayLabel={selectedCameraShortLabel}
                    onOpenChange={(open) => { if (open) markCameraPickerSeen(); }}
                  />
                  {showCameraNudge && cameraNudge}
                </div>
              )}
              <button onClick={onClearVideo} aria-label="Clear video" style={{ background: 'none', border: 'none', cursor: 'pointer', color: L.textMuted, padding: 0, lineHeight: 1, flexShrink: 0 }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Update affordance - shown when a result pill is live */}
        {hasResultPills && isStreaming && (
          <div
            data-testid="prompt-update-hint"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px 0',
              color: L.textMuted, fontSize: 12,
              userSelect: 'none', pointerEvents: 'none',
            }}
          >
            <span style={{ opacity: 0.65 }}>Update or expand your prompt in real-time&hellip;</span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.5, flexShrink: 0, marginTop: 1 }}>
              <path d="M5 1v7M2 5.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
        {/* Textarea - 3 lines min */}
        <div style={{ padding: compact ? '6px 10px 2px' : '10px 14px 4px', ...(fillHeight ? { flex: 1, display: 'flex', flexDirection: 'column' as const } : {}) }}>
          <textarea
            data-testid="prompt-textarea"
            data-sentry-mask
            ref={textareaRef}
            aria-label="Prompt input"
            value={prompt}
            readOnly={editMode && parseState === 'edit'}
            onChange={e => { if (!(editMode && parseState === 'edit')) onPromptChange(e.target.value); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (canSubmit) onSubmit(); } }}
            placeholder={isStreaming && hasResultPills ? 'Update the prompt\u2026' : 'Hey Darwin, tell me when you see...'}
            style={{
              width: '100%', border: 'none', outline: 'none', resize: 'none',
              background: 'transparent', fontSize: compact ? 14 : 16, color: L.textPrimary,
              lineHeight: '24px', fontFamily: 'Inter, -apple-system, sans-serif',
              minHeight: compact ? '36px' : isMobile ? '24px' : `${3 * 24}px`,
              maxHeight: compact ? '36px' : undefined,
              opacity: (editMode && parseState === 'edit') ? 0.5 : 1,
              ...(fillHeight ? { flex: 1 } : {}),
            }}
          />
          {editMode && parseState === 'edit' && (
            <div style={{
              fontSize: 11, color: L.textMuted,
              fontFamily: 'Inter, -apple-system, sans-serif',
              fontStyle: 'italic', marginTop: 2,
            }}>
              Prompt overridden by compiled query
            </div>
          )}
        </div>

        {/* PRI-494: stable one-line prompt preview (mobile 3-zone shell) —
            fixed height across ALL parse states, human-readable text only.
            Full lane card is opt-in via tap. */}
        {stablePromptPreview ? (
          <>
            <MobilePromptPreview
              parseState={parseState ?? 'idle'}
              compiledQuery={compiledQuery ?? null}
              parseError={parseError}
              expanded={previewExpanded}
              onToggleExpanded={() => setPreviewExpanded(v => !v)}
            />
            {previewExpanded && (parseState === 'lanes_visible' || parseState === 'edit') && compiledQuery !== undefined && (
              <CompiledQueryCard
                compiledQuery={compiledQuery ?? null}
                parseState={parseState ?? 'idle'}
                parseError={parseError}
                editMode={editMode}
                onEditToggle={(open) => { setEditMode(open); onEditModeToggle?.(open); }}
                onCompiledQueryChange={onCompiledQueryChange ?? (() => {})}
                onRestore={() => { setEditMode(false); onEditModeToggle?.(false); }}
                isMobile={isMobile}
              />
            )}
          </>
        ) : (
          <>
            {/* Compiled Query Card — expanded pill section */}
            {parseState && parseState !== 'idle' && compiledQuery !== undefined && (
              <CompiledQueryCard
                compiledQuery={compiledQuery ?? null}
                parseState={parseState ?? 'idle'}
                parseError={parseError}
                editMode={editMode}
                onEditToggle={(open) => { setEditMode(open); onEditModeToggle?.(open); }}
                onCompiledQueryChange={onCompiledQueryChange ?? (() => {})}
                onRestore={() => { setEditMode(false); onEditModeToggle?.(false); }}
                isMobile={isMobile}
              />
            )}
            {/* Ghost lane shown in IDLE state when no compiled query yet */}
            {(!parseState || parseState === 'idle') && (
              <div style={{
                margin: '0 10px 6px',
                padding: '6px 10px',
                borderRadius: 4,
                border: '1px dashed',
                borderColor: '#D4D0C8',
                opacity: 0.5,
                fontSize: 12,
                fontFamily: 'Inter, -apple-system, sans-serif',
                color: '#7A7669',
                fontStyle: 'italic',
              }}>
                type a prompt to see how Darwin reads it
              </div>
            )}
          </>
        )}

        {/* PRI-488 F7: batch composer — multi-prompt POST /v1/analyses/batch */}
        {showBatchComposer && publicVideoId !== undefined && (
          <BatchComposer publicVideoId={publicVideoId ?? null} onClose={() => setShowBatchComposer(false)} userPlan={planTier} />
        )}

        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 2,
          padding: compact ? '2px 8px 6px' : '6px 10px 10px',
        }}>
          {/* + button: opens VideoSourcePopover on all viewports.
               On mobile: 44px, primary way to add a video source.
               On desktop: 28px, sits alongside Webcam/File source toggles. */}
          <button
            data-testid="add-video-btn"
            onClick={() => _onOpenVideoMenu()}
            title="Add video"
            aria-label="Add video"
            style={{
              width: isMobile ? 44 : 28, height: isMobile ? 44 : 28, borderRadius: 8, border: 'none', flexShrink: 0,
              background: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: L.textMuted, transition: 'color 120ms',
            }}
          >
            <svg width={isMobile ? 18 : 14} height={isMobile ? 18 : 14} viewBox="0 0 18 18" fill="none">
              <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
          {/* Video Source Toggle: Webcam / File — hidden on mobile (+ button is the primary entry point) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, position: 'relative' as const }}>
            {/* First-use guide (2026-07-01): when no source is selected yet (camera
                permission never granted), point the user at the source toggles.
                Without this, a pre-selected blue "Webcam" pill looked already-active
                and users never clicked it, so the permission prompt never appeared. */}
            {sourceMode === null && !isStreaming && !isMobile && (
              <div
                data-testid="source-first-use-tooltip"
                role="status"
                style={{
                  position: 'absolute' as const,
                  top: 'calc(100% + 10px)', left: 0, zIndex: 150,
                  background: L.signal,
                  color: '#fff',
                  padding: '7px 11px', borderRadius: 8,
                  fontSize: 12, fontWeight: 500, lineHeight: '16px',
                  fontFamily: 'Inter, -apple-system, sans-serif',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 16px rgba(27,94,255,0.28)',
                  pointerEvents: 'none' as const,
                }}
              >
                Pick a source to start — Webcam will ask for camera access
                {/* arrow (points up at the source toggles) */}
                <div style={{
                  position: 'absolute' as const, bottom: '100%', left: 46,
                  width: 0, height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: `6px solid ${L.signal}`,
                }} />
              </div>
            )}
            {/* Label — compact to save horizontal space; hidden on mobile */}
            {!isMobile && <span style={{ fontSize: 10, color: L.textMuted, fontFamily: 'Inter, -apple-system, sans-serif', whiteSpace: 'nowrap', userSelect: 'none', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Source:</span>}
            {/* Webcam toggle + optional camera picker icon */}
            <div style={{ position: 'relative' as const, display: 'flex', alignItems: 'center' }} ref={sourceMode === 'webcam' ? cameraPickerRef : undefined}>
              {/* PRI-451: pre-stream discoverability nudge — anchored to the toolbar picker
                  whenever the AFTER-state chip switcher isn't on screen to host it. */}
              {showCameraNudge && !chipSwitcherVisible && cameraNudge}
              <button
                onClick={() => {
                  if (isStreaming) return; // lock source toggle during active stream
                  setSourceMode('webcam');
                  if (onSwitchToWebcam) {
                    // Re-acquire camera without fully releasing it (unlike onClearVideo which
                    // calls releaseCamera and nukes selectedDeviceId, preventing re-acquisition).
                    onSwitchToWebcam();
                  } else if (videoSource?.kind === 'file') {
                    onClearVideo();
                  }
                }}
                style={{
                  padding: '4px 8px', borderRadius: 6,
                  border: 'none',
                  background: sourceMode === 'webcam' ? `${L.signal}14` : 'transparent',
                  cursor: isStreaming ? 'default' : 'pointer',
                  opacity: isStreaming && sourceMode !== 'webcam' ? 0.4 : 1,
                  fontSize: 12, fontWeight: sourceMode === 'webcam' ? 600 : 400,
                  color: sourceMode === 'webcam' ? L.signal : L.textMuted,
                  fontFamily: 'Inter, -apple-system, sans-serif',
                  transition: 'all 130ms',
                  lineHeight: '20px',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                Webcam
                {/* Camera device picker icon - only shown when >1 camera available */}
                {videoCameraDevices.length >= 1 && (
                  <span
                    title="Choose which webcam to use"
                    onClick={(e) => { e.stopPropagation(); setSourceMode('webcam'); if (!showCameraPicker) markCameraPickerSeen(); setShowCameraPicker(p => !p); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: 4,
                      color: showCameraPicker ? L.signal : L.textMuted,
                      cursor: 'pointer',
                      transition: 'color 130ms',
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <rect x="1" y="4" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                      <circle cx="8" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                      <path d="M5.5 4V3a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.4"/>
                    </svg>
                  </span>
                )}
              </button>

              {/* Camera device picker dropdown */}
              {showCameraPicker && videoCameraDevices.length >= 1 && (
                <div style={{
                  position: 'absolute' as const, top: 'calc(100% + 6px)', left: 0, zIndex: 200,
                  background: L.bgSecondary,
                  border: `1px solid ${L.border}`,
                  borderRadius: 10,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                  padding: '6px 4px',
                  minWidth: 220,
                }}>
                  {videoCameraDevices.map((device) => {
                    const isSelected = (selectedCameraId ?? '') === device.deviceId ||
                      (!selectedCameraId && videoCameraDevices[0].deviceId === device.deviceId);
                    return (
                      <button
                        key={device.deviceId}
                        onClick={() => { onSelectCamera?.(device.deviceId); setShowCameraPicker(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          width: '100%', padding: '8px 12px',
                          background: isSelected ? `${L.signal}14` : 'transparent',
                          border: 'none', borderRadius: 7,
                          cursor: 'pointer',
                          textAlign: 'left' as const,
                          fontSize: 13, color: L.textPrimary,
                          fontFamily: 'Inter, -apple-system, sans-serif',
                          transition: 'background 100ms',
                        }}
                      >
                        {/* Dot indicator: filled blue = selected, empty ring = not */}
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                          {isSelected
                            ? <circle cx="5" cy="5" r="5" fill="#3B82F6" />
                            : <circle cx="5" cy="5" r="4" stroke={L.textMuted} strokeWidth="1.5" />}
                        </svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {device.label || `Camera ${videoCameraDevices.indexOf(device) + 1}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <SubtleToggle label="File" active={sourceMode === 'file'} disabled={isStreaming} onClick={() => { if (isStreaming) return; setSourceMode('file'); onSwitchToFile?.(); onRequestFile?.(); }} />
          </div>

          {/* File-mode sub-toggle: Process as live stream / Process offline.
              PRI-330: large={isMobile} ensures ≥44pt tap targets on mobile. */}
          {sourceMode === 'file' && videoSource != null && videoSource.kind !== 'stream' && (
            <>
              <ToolSep />
              <SubtleToggle label="Live" large={isMobile} active={!processOffline} onClick={() => {
                if (processOffline) trackBatchModeToggled({ enabled: false, user_plan: planTier });
                onProcessOfflineChange(false);
              }} title="Stream file in real time (results as it plays)" />
              <SubtleToggle label="Batch" large={isMobile} active={processOffline} onClick={() => {
                if (!processOffline) trackBatchModeToggled({ enabled: true, user_plan: planTier });
                onProcessOfflineChange(true);
              }} title="Process offline (all results at once, slightly more accurate)" />
            </>
          )}

          {/* PRI-488 F7: Multi-prompt batch toggle — file mode with an uploaded video */}
          {publicVideoId !== undefined && sourceMode === 'file' && videoSource != null && videoSource.kind !== 'stream' && !isStreaming && (
            <>
              <ToolSep />
              <SubtleToggle
                label="Multi"
                large={isMobile}
                active={showBatchComposer}
                onClick={() => setShowBatchComposer(v => !v)}
                title="Ask 2–10 questions about this video in one batch — extras 50% off"
              />
            </>
          )}

          {/* Spacer pushes action buttons toward right */}
          <div style={{ flex: 1 }} />

          <button
            onClick={() => setShowAdvanced(a => !a)}
            title="Advanced Settings"
            style={{
              padding: isMobile ? '6px 8px' : '4px 8px', borderRadius: 6,
              border: 'none', background: showAdvanced ? `${L.signal}14` : 'transparent',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 12, color: showAdvanced ? L.signal : L.textMuted,
              fontFamily: 'Inter, -apple-system, sans-serif',
              lineHeight: '20px',
              minHeight: isMobile ? 44 : undefined,
              transition: 'all 130ms',
            }}
          >
            Advanced
            <svg width="8" height="8" viewBox="0 0 9 9" fill="none" style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
              <path d="M1.5 3l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Submit button - active: success green; idle: muted grey. Text always shown on desktop. */}
          <button
            data-testid="submit-prompt-btn"
            onClick={onSubmit}
            disabled={!canSubmit}
            aria-label="Submit prompt"
            title={canSubmit ? 'Submit your prompt' : 'Add a video and prompt first'}
            style={{
              height: isMobile ? 44 : 32, borderRadius: 8,
              padding: isMobile ? undefined : '0 14px',
              width: isMobile ? 44 : undefined,
              minWidth: isMobile ? 44 : undefined,
              background: promptSent ? '#16A34A' : canSubmit ? '#16A34A' : L.border,
              border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 150ms', flexShrink: 0,
              transform: promptSent ? 'scale(0.9)' : 'scale(1)',
              opacity: canSubmit ? 1 : 0.5,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 11V1M1 6l5-5 5 5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {!isMobile && (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>Submit</span>
            )}
          </button>
        </div>

        {/* Advanced Settings tray */}
        {showAdvanced && (
          <div style={{
            borderTop: `1px solid ${L.border}`,
            padding: isMobile ? '10px 12px 12px' : '8px 14px',
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 8 : 2,
            flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
              <span title="Choose a model to run"><ModelToggle /></span>

              <ToolSep />

              {/* Mic button */}
              <button title="Speak your prompt instead of typing" aria-label="Voice input" style={{
                width: 32, height: 32, borderRadius: 8,
                border: `1px solid ${L.border}`,
                background: 'transparent', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: L.textMuted, flexShrink: 0,
              }}>
                <svg width="11" height="15" viewBox="0 0 11 15" fill="none">
                  <rect x="3.5" y="0.5" width="4" height="7" rx="2" stroke="currentColor" strokeWidth="1.2"/>
                  <path d="M0.5 7A5 5 0 0 0 10.5 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  <line x1="5.5" y1="12" x2="5.5" y2="14.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ── Cost preview (PRI-488 F1: validate_only dry-run) ── */}
        {costPreview && !isStreaming && (
          <div data-testid="cost-preview" style={{ borderTop: `1px solid ${L.border}`, padding: '5px 14px 6px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 24 }}>
            {costPreview.rephraseHint ? (
              <span data-testid="cost-preview-rephrase" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#D97706' }}>
                ⚠ {costPreview.rephraseHint}
              </span>
            ) : (
              <>
                <span data-testid="cost-preview-estimate" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: costPreview.insufficient ? '#DC2626' : L.textMuted }}>
                  {costPreview.estimatedSeconds != null
                    ? `~${costPreview.estimatedSeconds}s ≈ ${costPreview.estimatedSeconds} credit${costPreview.estimatedSeconds === 1 ? '' : 's'}`
                    : 'Estimating cost…'}
                  {costPreview.estimatedSeconds != null && costPreview.balanceSeconds != null
                    ? ` (of ${costPreview.balanceSeconds.toLocaleString()} remaining)`
                    : ''}
                </span>
                {costPreview.assessable && (
                  <span data-testid="cost-preview-assessable" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#16A34A' }}>
                    query is assessable ✓
                  </span>
                )}
                {costPreview.insufficient && (
                  <a href="/dashboard/billing" data-testid="cost-preview-insufficient" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#DC2626', textDecoration: 'none' }}>
                    Not enough credits — add credits →
                  </a>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Credits balance bar (PRI-437 P8) ── */}
        {quotaBar && (
          <div style={{ borderTop: `1px solid ${L.border}`, padding: '5px 14px 8px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 28 }}>
            {quotaBar.loading ? (
              <div style={{ height: 10, width: 180, background: L.bgCode, borderRadius: 4 }} />
            ) : creditsExhausted ? (
              quotaBar.anonTrial ? (
                <>
                  <span data-testid="anon-trial-exhausted-bar" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#DC2626' }}>
                    Free trial used
                  </span>
                  <span style={{ color: L.border, fontSize: 10 }}>·</span>
                  <a href="/signup" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.signal, textDecoration: 'none' }} onClick={() => trackUpgradeFromQuotaWall('anon_trial_exhausted')}>Sign up free →</a>
                </>
              ) : (
              <>
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#DC2626' }}>
                  Out of credits
                </span>
                <span style={{ color: L.border, fontSize: 10 }}>·</span>
                <a href="/dashboard/billing" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.signal, textDecoration: 'none' }} onClick={() => trackUpgradeFromQuotaWall('credits_exhausted')}>Add credits →</a>
              </>
              )
            ) : (
              <>
                {quotaBar.balanceSeconds !== null && (
                  <span data-testid="quota-bar-balance" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: creditsLow ? '#D97706' : L.textMuted }}>
                    ⚡ {quotaBar.balanceSeconds.toLocaleString()}s {quotaBar.anonTrial ? 'free trial' : 'credit'} remaining
                  </span>
                )}
                {isStreaming && streamingMetering && (
                  <>
                    <span style={{ color: L.border, fontSize: 10 }}>·</span>
                    <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.textMuted }}>
                      ● {Math.max(0, streamingMetering.daily_used_s)}s used today
                    </span>
                    {/* PRI-488 F4: live countdown from the canonical session_remaining_s
                        metering field (the deprecated balance_s alias is never read). */}
                    {streamingMetering.session_remaining_s != null && (
                      <>
                        <span style={{ color: L.border, fontSize: 10 }}>·</span>
                        <SessionCountdown sessionRemainingS={streamingMetering.session_remaining_s} />
                      </>
                    )}
                  </>
                )}
                {isStreaming && isFileStream && fileDuration != null && fileDuration > 0 && (
                  <>
                    <span style={{ color: L.border, fontSize: 10 }}>·</span>
                    <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.textMuted }}>
                      ▶ {Math.floor(fileCurrentTime ?? 0)}s / {Math.floor(fileDuration)}s
                    </span>
                  </>
                )}
                {creditsLow && (
                  <>
                    <span style={{ color: L.border, fontSize: 10 }}>·</span>
                    {quotaBar.anonTrial ? (
                      <a href="/signup" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.signal, textDecoration: 'none' }} onClick={() => trackUpgradeFromQuotaWall('anon_trial_low')}>Sign up to keep going →</a>
                    ) : (
                      <a href="/dashboard/billing" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.signal, textDecoration: 'none' }} onClick={() => trackUpgradeFromQuotaWall('warning_banner')}>Add credits →</a>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** Subtle text toggle - same style for model/quality controls */
export function SubtleToggle({ label, active, onClick, large = false, title, disabled = false }: { label: string; active: boolean; onClick: () => void; large?: boolean; title?: string; disabled?: boolean }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => { if (!disabled) setH(true); }}
      onMouseLeave={() => setH(false)}
      title={title}
      disabled={disabled}
      style={{
        padding: large ? '8px 12px' : '4px 8px', minHeight: large ? 44 : undefined, borderRadius: 6,
        border: 'none', background: active ? `${L.signal}14` : h ? `${L.textMuted}12` : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled && !active ? 0.4 : 1,
        fontSize: 12, fontWeight: active ? 600 : 400,
        color: active ? L.signal : h ? L.textPrimary : L.textMuted,
        fontFamily: 'Inter, -apple-system, sans-serif',
        transition: 'all 130ms',
        lineHeight: '20px',
      }}
    >
      {label}
    </button>
  );
}

/** Vertical separator between tool groups */
export function ToolSep() {
  return <div style={{ width: 1, height: 16, background: L.border, margin: '0 4px', flexShrink: 0 }} />;
}

/** Model picker - same subtle style, with a small dropdown chevron */
export function ModelToggle() {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
        style={{
          padding: '4px 8px', borderRadius: 6,
          border: 'none', background: h ? `${L.textMuted}12` : 'transparent',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 12, color: h ? L.textPrimary : L.textMuted,
          fontFamily: 'Inter, -apple-system, sans-serif',
          lineHeight: '20px',
          transition: 'all 130ms',
        }}
      >
        {MODEL_DISPLAY_NAME}
        <span style={{
          fontSize: 9, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
          color: L.signal, background: `${L.signal}14`, borderRadius: 4, padding: '1px 5px',
          lineHeight: '14px',
        }}>
          {modelStatusLabel()}
        </span>
        <svg width="8" height="8" viewBox="0 0 9 9" fill="none">
          <path d="M1.5 3l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0,
            background: L.bgSurface, border: `1px solid ${L.border}`,
            borderRadius: 10, padding: 8, minWidth: 210,
            boxShadow: '0 6px 20px rgba(0,0,0,0.1)', zIndex: 100,
          }}>
            <div style={{ padding: '8px 10px', borderRadius: 7, background: `${L.signal}0f` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: L.textPrimary, fontFamily: '"JetBrains Mono", monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
                {MODEL_DISPLAY_NAME}
                <span style={{
                  fontSize: 9, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: L.signal, background: `${L.signal}14`, borderRadius: 4, padding: '1px 5px',
                  fontFamily: 'Inter, -apple-system, sans-serif', lineHeight: '14px',
                }}>
                  {modelStatusLabel()}
                </span>
              </div>
              <div style={{ fontSize: 11, color: L.textMuted, marginTop: 2 }}>JEPA predictive world model · early access — detection quality still evolving</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Video Source Popover
// ─────────────────────────────────────────────────────────────────────────────

export function VideoSourcePopover({ onClose, onUpload, onStream, isMobile = false, devices, selectedDeviceId, onSelectDevice }: {
  onClose: () => void;
  onUpload: () => void;
  onStream: (deviceId?: string) => void;
  isMobile?: boolean;
  devices?: MediaDeviceInfo[];
  selectedDeviceId?: string | null;
  onSelectDevice?: (deviceId: string) => void;
}) {
  const [showCameraList, setShowCameraList] = useState(false);
  const cameraDevices = devices?.filter(d => d.kind === 'videoinput') ?? [];
  const hasMultipleCameras = cameraDevices.length > 1;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 299 }} />
      <div data-testid="video-source-popover" style={{
        ...(isMobile
          ? { position: 'fixed' as const, bottom: 'calc(49px + env(safe-area-inset-bottom, 0px) + 8px)', left: 12, right: 12, zIndex: 300 }
          : { position: 'absolute' as const, bottom: 'calc(100% + 8px)', left: 0, zIndex: 300 }
        ),
        background: L.bgSurface, border: `1px solid ${L.border}`,
        borderRadius: 12, padding: 6, minWidth: 210,
        boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
      }}>
        <div style={{ padding: '6px 10px 2px', fontSize: 9, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: L.textMuted, fontWeight: 600 }}>Streaming source:</div>
        <SrcOption
          icon={<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.2"/><circle cx="7.5" cy="7.5" r="2.5" fill="currentColor" opacity="0.4"/><circle cx="7.5" cy="7.5" r="1" fill="currentColor"/></svg>}
          label="Live Camera" sub={hasMultipleCameras ? 'Select camera...' : 'Stream from webcam'}
          onClick={() => {
            if (hasMultipleCameras) {
              setShowCameraList(prev => !prev);
            } else {
              onStream(); onClose();
            }
          }}
          large={isMobile}
        />
        {/* Camera device picker - flip button on mobile, radio list on desktop */}
        {showCameraList && (hasMultipleCameras || !selectedDeviceId) && (
          isMobile ? (
            <div style={{ padding: '4px 12px 4px' }}>
              <button
                data-testid="camera-flip-btn"
                onClick={() => {
                  const currentIdx = cameraDevices.findIndex(d => d.deviceId === selectedDeviceId);
                  const nextIdx = (currentIdx + 1) % cameraDevices.length;
                  onSelectDevice?.(cameraDevices[nextIdx].deviceId);
                  onStream(cameraDevices[nextIdx].deviceId);
                  onClose();
                }}
                aria-label="Switch camera"
                style={{
                  width: 44, height: 44, borderRadius: 8,
                  border: `1px solid ${L.border}`, background: 'transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: L.textMuted,
                }}
              >
                {/* Camera flip icon */}
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="5" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                  <circle cx="10" cy="11" r="3" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M7 3l1.5-1.5h3L13 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 8l2-2m0 0l-2-2m2 2h-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <div style={{ paddingLeft: 12, paddingBottom: 4 }}>
              {cameraDevices.map((d, i) => {
                const isSelected = d.deviceId === selectedDeviceId;
                const label = d.label || `Camera ${i + 1}`;
                return (
                  <div
                    key={d.deviceId}
                    onClick={() => {
                      onSelectDevice?.(d.deviceId);
                      onStream(d.deviceId);
                      onClose();
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                      background: isSelected ? `${L.signal}18` : 'transparent',
                      transition: 'background 100ms',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${L.signal}18`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected ? `${L.signal}18` : 'transparent'; }}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: isSelected ? L.signal : 'transparent',
                      border: isSelected ? 'none' : `1.5px solid ${L.textMuted}`,
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontFamily: '"JetBrains Mono", monospace', fontSize: 11,
                      color: isSelected ? L.textPrimary : L.textMuted,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{label}</span>
                  </div>
                );
              })}
            </div>
          )
        )}
        <SrcOption
          icon={<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M7.5 5v5M5 7.5h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>}
          label="Local File" sub="MP4, MOV, AVI, WebM..."
          onClick={() => { onUpload(); onClose(); }}
          large={isMobile}
        />
        {/* PRI-437 P8: quality selection removed — one metered rate, no user-facing quality tiers. */}
      </div>
    </>
  );
}

export function SrcOption({ icon, label, sub, onClick, large = false }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void; large?: boolean }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      width: '100%', padding: large ? '12px 16px' : '9px 12px', minHeight: large ? 48 : undefined, borderRadius: 8,
      border: 'none', cursor: 'pointer', textAlign: 'left',
      background: h ? `${L.signal}0f` : 'transparent',
      color: L.textMuted, transition: 'background 120ms',
    }}>
      <div style={{ flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: L.textPrimary }}>{label}</div>
        <div style={{ fontSize: 11, color: L.textMuted, marginTop: 1 }}>{sub}</div>
      </div>
    </button>
  );
}
